import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  FormControlLabel,
  LinearProgress,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { DepotInput, DepotSummary, TenantProfile } from '../../../models/userManagement';
import { routingJobsApi } from '../../../api/routingJobsApi';

const MODEL_TYPES = ['SAVINGS', 'WASTE_MGT', 'REWORLD_WST_MGT', 'SLOTTED', 'SID', 'PRY', 'GEMBA_SLOTTED'];
const PRY_VEHICLE_TYPE_COST_KEY = 'vehicle_type_cost';
const SOURCE_LATITUDE_KEY = 'sourceLatitude';
const SOURCE_LONGITUDE_KEY = 'sourceLongitude';
const JUMBOBUY_MODEL_TYPES = new Set(['SLOTTED', 'SID', 'PRY']);

interface DepotDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: DepotInput) => Promise<void> | void;
  tenants: TenantProfile[];
  initialValues?: DepotSummary;
  defaultTenantId?: string;
}

const defaultValues: DepotInput = {
  depotId: '',
  name: '',
  tenantId: '',
  modelType: 'SAVINGS',
  active: true,
  configFilesMap: {},
};

const DepotDialog: React.FC<DepotDialogProps> = ({ open, onClose, onSubmit, tenants, initialValues, defaultTenantId }) => {
  const [values, setValues] = useState<DepotInput>(defaultValues);
  const [submitting, setSubmitting] = useState(false);
  const [configUpload, setConfigUpload] = useState<{
    uploading: boolean;
    fileName?: string;
    error?: string;
  }>({ uploading: false });

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === values.tenantId),
    [tenants, values.tenantId]
  );

  const allowedModelOptions = useMemo(() => {
    if (selectedTenant && selectedTenant.allowedModelTypes?.length) {
      return selectedTenant.allowedModelTypes;
    }
    return MODEL_TYPES;
  }, [selectedTenant]);

  const validationErrors = useMemo(() => {
    const errors: Partial<Record<keyof DepotInput, string>> = {};
    if (!values.depotId.trim()) {
      errors.depotId = 'Depot ID is required';
    }
    if (!values.name.trim()) {
      errors.name = 'Depot name is required';
    }
    if (!values.tenantId) {
      errors.tenantId = 'Select a tenant';
    }
    if (!values.modelType) {
      errors.modelType = 'Select a model type';
    } else if (allowedModelOptions.length && !allowedModelOptions.includes(values.modelType)) {
      errors.modelType = 'Model type must be allowed for this tenant';
    }
    if (values.modelType === 'PRY' && !values.configFilesMap?.[PRY_VEHICLE_TYPE_COST_KEY]) {
      errors.configFilesMap = 'Upload vehicle type cost config for PRY';
    }
    if (JUMBOBUY_MODEL_TYPES.has(values.modelType)) {
      const lat = Number(values.configFilesMap?.[SOURCE_LATITUDE_KEY]);
      const lng = Number(values.configFilesMap?.[SOURCE_LONGITUDE_KEY]);
      if (!values.configFilesMap?.[SOURCE_LATITUDE_KEY] || !Number.isFinite(lat) || lat < -90 || lat > 90) {
        errors.configFilesMap = 'Enter valid source latitude and longitude for JumboBuy depots';
      } else if (!values.configFilesMap?.[SOURCE_LONGITUDE_KEY] || !Number.isFinite(lng) || lng < -180 || lng > 180) {
        errors.configFilesMap = 'Enter valid source latitude and longitude for JumboBuy depots';
      }
    }
    return errors;
  }, [values, allowedModelOptions]);

  const isValid = useMemo(() => Object.values(validationErrors).every((msg) => !msg), [validationErrors]);

  useEffect(() => {
    if (initialValues) {
      setValues({
        depotId: initialValues.depotId,
        name: initialValues.name,
        tenantId: initialValues.tenantId,
        modelType: initialValues.modelType,
        active: initialValues.active,
        configFilesMap: initialValues.configFilesMap ?? {},
      });
    } else {
      setValues({ ...defaultValues, tenantId: defaultTenantId || '' });
    }
    setConfigUpload({ uploading: false });
  }, [initialValues, open, defaultTenantId]);

  useEffect(() => {
    if (allowedModelOptions.length && !allowedModelOptions.includes(values.modelType)) {
      setValues((prev) => ({ ...prev, modelType: allowedModelOptions[0] }));
    }
  }, [allowedModelOptions, values.modelType]);

  const handleChange = (field: keyof DepotInput, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfigValueChange = (key: string, value: string) => {
    setValues((prev) => ({
      ...prev,
      configFilesMap: {
        ...(prev.configFilesMap ?? {}),
        [key]: value,
      },
    }));
  };

  const handleVehicleTypeCostUpload = async (file: File) => {
    if (!values.tenantId || !values.modelType) {
      setConfigUpload({ uploading: false, fileName: file.name, error: 'Select tenant and model type first' });
      return;
    }
    setConfigUpload({ uploading: true, fileName: file.name });
    try {
      const response = await routingJobsApi.uploadTenantFile(
        values.tenantId,
        values.modelType,
        PRY_VEHICLE_TYPE_COST_KEY,
        file
      );
      const storageRef = response.storagePath || response.storageId;
      setValues((prev) => ({
        ...prev,
        configFilesMap: {
          ...(prev.configFilesMap ?? {}),
          [PRY_VEHICLE_TYPE_COST_KEY]: storageRef,
        },
      }));
      setConfigUpload({ uploading: false, fileName: file.name });
    } catch (error: any) {
      setConfigUpload({
        uploading: false,
        fileName: file.name,
        error: error?.message || 'Upload failed',
      });
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(values);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const isEditMode = Boolean(initialValues);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditMode ? 'Edit Depot' : 'Create Depot'}</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Depot ID"
              value={values.depotId}
              onChange={(event) => handleChange('depotId', event.target.value)}
              required
              disabled={isEditMode}
              error={Boolean(validationErrors.depotId)}
              helperText={validationErrors.depotId}
            />
            <TextField
              label="Name"
              value={values.name}
              onChange={(event) => handleChange('name', event.target.value)}
              required
              error={Boolean(validationErrors.name)}
              helperText={validationErrors.name}
            />
            <FormControl fullWidth error={Boolean(validationErrors.tenantId)}>
              <InputLabel id="depot-tenant-label">Tenant</InputLabel>
              <Select
                labelId="depot-tenant-label"
                label="Tenant"
                value={values.tenantId || ''}
                onChange={(event) => handleChange('tenantId', event.target.value)}
                required
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {validationErrors.tenantId || 'Select the tenant owning this depot'}
              </FormHelperText>
            </FormControl>
            <FormControl fullWidth error={Boolean(validationErrors.modelType)}>
              <InputLabel id="model-type-label">Model Type</InputLabel>
              <Select
                labelId="model-type-label"
                label="Model Type"
                value={values.modelType}
                onChange={(event) => handleChange('modelType', event.target.value)}
              >
                {allowedModelOptions.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {validationErrors.modelType || 'Choose a model type supported by the tenant'}
              </FormHelperText>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={values.active ?? true} onChange={(event) => handleChange('active', event.target.checked)} />}
              label="Active"
            />
            {JUMBOBUY_MODEL_TYPES.has(values.modelType) && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Source Latitude"
                  value={values.configFilesMap?.[SOURCE_LATITUDE_KEY] ?? ''}
                  onChange={(event) => handleConfigValueChange(SOURCE_LATITUDE_KEY, event.target.value)}
                  required
                  type="number"
                  inputProps={{ min: -90, max: 90, step: 0.000001 }}
                  error={Boolean(validationErrors.configFilesMap)}
                  helperText="Depot start latitude"
                  fullWidth
                />
                <TextField
                  label="Source Longitude"
                  value={values.configFilesMap?.[SOURCE_LONGITUDE_KEY] ?? ''}
                  onChange={(event) => handleConfigValueChange(SOURCE_LONGITUDE_KEY, event.target.value)}
                  required
                  type="number"
                  inputProps={{ min: -180, max: 180, step: 0.000001 }}
                  error={Boolean(validationErrors.configFilesMap)}
                  helperText="Depot start longitude"
                  fullWidth
                />
              </Stack>
            )}
            {values.modelType === 'PRY' && (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: validationErrors.configFilesMap || configUpload.error ? 'error.main' : 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2">Vehicle Type Cost</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {configUpload.fileName ||
                          (values.configFilesMap?.[PRY_VEHICLE_TYPE_COST_KEY] ? 'Config file uploaded' : 'No file selected')}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      disabled={configUpload.uploading || !values.tenantId}
                    >
                      {values.configFilesMap?.[PRY_VEHICLE_TYPE_COST_KEY] ? 'Replace' : 'Upload'}
                      <input
                        type="file"
                        accept=".csv"
                        hidden
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) {
                            handleVehicleTypeCostUpload(file);
                          }
                        }}
                      />
                    </Button>
                  </Stack>
                  {configUpload.uploading && <LinearProgress />}
                  {(validationErrors.configFilesMap || configUpload.error) && (
                    <Alert severity="error">
                      {configUpload.error || validationErrors.configFilesMap}
                    </Alert>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting || !isValid}>
            {isEditMode ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default DepotDialog;
